import React from 'react';
import './App.css';
import { motion } from 'framer-motion';
import { FaLinkedin, FaGithub, FaEnvelope, FaJava, FaReact, FaDatabase, FaDocker, FaAngular, FaUserTie, FaGraduationCap, FaChevronDown, FaBars, FaTimes, FaSun, FaMoon, FaArrowUp, FaCheckCircle, FaPlay } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import { SiSpring, SiPostman } from 'react-icons/si';

const translations = {
  es: {
    nav: [
      { id: 'home', label: 'Inicio' },
      { id: 'about', label: 'Sobre mí' },
      { id: 'skills', label: 'Habilidades' },
      { id: 'experience', label: 'Experiencia' },
      { id: 'projects', label: 'Proyectos' },
      { id: 'contact', label: 'Contacto' },
    ],
    aboutTitle: 'Sobre mí',
    aboutP1: '¿Buscas a alguien que lleve tu proyecto al siguiente nivel?',
    aboutP2: 'Desarrollo aplicaciones web a medida con Java y Spring Boot en el backend y React en el frontend, desde la idea inicial hasta el despliegue en producción.',
    aboutP3: 'Trabajo con arquitectura de microservicios, bases de datos como MySQL y OracleSQL y despliegues con Docker, cuidando rendimiento, seguridad y mantenimiento a largo plazo.',
    aboutP4: 'Me involucro en cada proyecto como si fuera propio, aportando ideas, compromiso y una comunicación clara. Hoy compagino mi trabajo con proyectos freelance, ayudando a empresas y profesionales a digitalizar procesos y automatizar tareas con aplicaciones web hechas a medida.',
    skills: 'Habilidades',
    skillsList: [
      { label: 'Java', icon: <FaJava /> },
      { label: 'Spring Boot', icon: <SiSpring /> },
      { label: 'React', icon: <FaReact /> },
      { label: 'MySQL', icon: <FaDatabase /> },
      { label: 'Docker', icon: <FaDocker /> },
      { label: 'Angular', icon: <FaAngular /> },
    ],
    experience: 'Experiencia',
    experienceList: [
      {
        title: 'Desarrollador Fullstack',
        role: 'Empleado',
        type: 'Tiempo completo',
        date: '2021 - Actualidad',
        place: 'Empresa X',
        desc: 'Desarrollo de aplicaciones web modernas usando React y Spring Boot. Integración de microservicios y despliegue con Docker.'
      },
      {
        title: 'Desarrollador Backend',
        role: 'Prácticas',
        type: 'Prácticas',
        date: '2020 - 2021',
        place: 'Empresa Y',
        desc: 'Implementación de APIs REST y gestión de bases de datos MySQL.'
      }
    ],
    projects: 'Proyectos',
    projectsIntro: 'Vídeo demo + repositorio en GitHub. En el README de cada proyecto verás cómo desplegarlo en local',
    projectsStackIntro: 'Stack común: Java · Spring Boot · React · Docker · MySQL/OracleSQL',
    projectsMoreInfoNote: 'Nota: en la pantalla inicial de cada proyecto hay más información sobre funcionalidades y arquitectura.',
    projectBtnVideo: 'Ver vídeo',
    projectBtnGithub: 'GitHub',
    contact: 'Contacto',
    send: 'Enviar',
    name: 'Tu nombre',
    email: 'Tu email',
    message: 'Tu mensaje',
    cv: 'Ver CV',
    feedbackSuccess: '¡Mensaje enviado correctamente!',
    feedbackError: 'Error al enviar el mensaje. Inténtalo de nuevo.'
  },
  en: {
    nav: [
      { id: 'home', label: 'Home' },
      { id: 'about', label: 'About' },
      { id: 'skills', label: 'Skills' },
      { id: 'experience', label: 'Experience' },
      { id: 'projects', label: 'Projects' },
      { id: 'contact', label: 'Contact' },
    ],
    aboutTitle: 'About me',
    aboutP1: 'Looking for someone to take your project to the next level?',
    aboutP2: 'I build tailor-made web applications with Java and Spring Boot on the backend and React on the frontend, from the initial idea to production deployment.',
    aboutP3: 'I work with microservices architecture, databases such as MySQL and OracleSQL, and Docker-based deployments, focusing on performance, security and long-term maintainability.',
    aboutP4: 'I treat every project as if it were my own, bringing ideas, commitment and clear communication. Today I combine my job with freelance projects, helping companies and professionals digitalise processes and automate tasks with custom web applications.',
    skills: 'Skills',
    skillsList: [
      { label: 'Java', icon: <FaJava /> },
      { label: 'Spring Boot', icon: <SiSpring /> },
      { label: 'React', icon: <FaReact /> },
      { label: 'MySQL', icon: <FaDatabase /> },
      { label: 'Docker', icon: <FaDocker /> },
      { label: 'Angular', icon: <FaAngular /> },
    ],
    experience: 'Experience',
    experienceList: [
      {
        title: 'Fullstack Developer',
        role: 'Employee',
        type: 'Full time',
        date: '2021 - Present',
        place: 'Company X',
        desc: 'Development of modern web applications using React and Spring Boot. Microservices integration and deployment with Docker.'
      },
      {
        title: 'Backend Developer',
        role: 'Internship',
        type: 'Internship',
        date: '2020 - 2021',
        place: 'Company Y',
        desc: 'Implementation of REST APIs and MySQL database management.'
      }
    ],
    projects: 'Projects',
    projectsIntro: 'Demo video (2–3 min) + GitHub repository. Check each project README for local setup (Docker).',
    projectsStackIntro: 'Common stack: Java · Spring Boot · React · Docker · MySQL/OracleSQL',
    projectsMoreInfoNote: 'Note: each project home screen includes more details about features and architecture.',
    projectBtnVideo: 'Watch video',
    projectBtnGithub: 'GitHub',
    contact: 'Contact',
    send: 'Send',
    name: 'Your name',
    email: 'Your email',
    message: 'Your message',
    cv: 'View CV',
    feedbackSuccess: 'Message sent successfully!',
    feedbackError: 'Error sending message. Please try again.'
  }
};

export default function App() {
  const formRef = React.useRef();
  const [formStatus, setFormStatus] = React.useState(null);
  const hoverAudio = React.useRef(null);
  const successAudio = React.useRef(null);
  const toggleAudio = React.useRef(null);
  const slideAudio = React.useRef(null);
  const langAudio = React.useRef(null);
  const [openExp, setOpenExp] = React.useState([]);
  const [openEdu, setOpenEdu] = React.useState([]);
  const [audioEnabled, setAudioEnabled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true;
  });
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [lang, setLang] = React.useState(() => {
    const saved = localStorage.getItem('lang');
    return saved ? saved : 'es';
  });
  const [activeProject, setActiveProject] = React.useState(null);

  const headerTitle = {
    es: 'Desarrollador Fullstack',
    en: 'Fullstack Developer'
  };
  const headerDesc = {
    es: 'Desarrollo aplicaciones web a medida con Java, Spring Boot y React, uniendo un backend sólido, un frontend moderno y despliegues con Docker para que tu proyecto sea estable, escalable y fácil de mantener.',
    en: 'I build custom web applications with Java, Spring Boot and React, combining a solid backend, a modern frontend and Docker-based deployments so your project is stable, scalable and easy to maintain.'
  };

  // Definir variables dependientes de lang aquí
  const navLinks = translations[lang].nav;
  const skills = [
    { icon: <FaJava />, label: 'Java' },
    { icon: <SiSpring color="#ff4c4c" />, label: 'Spring Boot' },
    { icon: <FaReact />, label: 'React' },
    { icon: <FaAngular />, label: 'Angular' },
    { icon: <FaDatabase />, label: 'MySQL' },
    { icon: <FaDocker />, label: 'Docker' },
    // Segunda línea
    { icon: <FaDatabase />, label: 'OracleSQL' },
    { icon: <FaCheckCircle />, label: 'JUnit' },
    { icon: <FaCheckCircle />, label: 'Mockito' },
    { icon: <FaGithub />, label: 'Git' },
    { icon: <FaGithub />, label: 'GitHub' },
    { icon: <SiPostman />, label: 'Postman' },
  ];
  const experience = [
    {
      icon: <FaUserTie />,
      title: { es: 'Freelancer ', en: 'Freelancer ' },
      role: { es: 'Desarrollador Fullstack', en: 'Fullstack Developer' },
      type: { es: 'Remoto', en: 'Remote' },
      date: { es: '2025 – Actualidad', en: '2025 – Present' },
      place: { es: 'España', en: 'Spain' },
      desc: {
        es: 'Desarrollo de aplicaciones web a medida para clientes de distintos sectores, utilizando Java, Spring Boot, microservicios, Docker, bases de datos (MySQL, OracleSQL) y React. Diseño de interfaces atractivas, optimización de rendimiento, despliegue profesional y soluciones adaptadas a cada necesidad.',
        en: 'Development of custom web applications for clients from various sectors, using Java, Spring Boot, microservices, Docker, databases (MySQL, OracleSQL), and React. Design of attractive interfaces, performance optimization, professional deployment, and solutions tailored to each need.'
      }
    },
    {
      icon: <FaUserTie />,
      title: { es: 'Indra (Minsait) ', en: 'Indra (Minsait) ' },
      role: { es: 'Desarrollador Fullstack', en: 'Fullstack Developer' },
      type: { es: 'Remoto', en: 'Remote' },
      date: { es: 'Noviembre 2025 - Actualidad', en: 'November 2025 - Present' },
      place: { es: 'España', en: 'Spain' },
      desc: {
        es: 'Desarrollo fullstack de aplicaciones con Java y Spring Boot, trabajando con APIs REST, arquitectura de microservicios, Docker y bases de datos (MySQL/OracleSQL). Integración y despliegue con CI/CD usando Jenkins, gestión de proyectos y tickets en Jira, control de versiones y flujo de trabajo en GitLab, y colaboración en equipo con buenas prácticas de calidad.',
        en: 'Fullstack development with Java and Spring Boot, working with REST APIs, microservices architecture, Docker, and databases (MySQL/OracleSQL). CI/CD integration and deployments using Jenkins, project and ticket management in Jira, version control and workflows in GitLab, and team collaboration following quality best practices.'
      }
    },
    {
      icon: <FaUserTie />,
      title: { es: 'Astibot S.L. ', en: 'Astibot S.L. ' },
      role: { es: 'Desarrollador Fullstack', en: 'Fullstack Developer' },
      type: { es: 'Remoto', en: 'Remote' },
      date: { es: 'Junio 2024 - Noviembre 2025', en: 'June 2024 - November 2025' },
      place: { es: 'Valladolid, España', en: 'Valladolid, Spain' },
      desc: {
        es: 'Responsable del desarrollo de mis propios proyectos, en su mayoría destinados a la Administración Pública. Diseño e implemento soluciones con arquitectura de microservicios, utilizando Java, Spring Boot, Docker y bases de datos (MySQL, OracleSQL). Desarrollo tanto el backend como el frontend de las aplicaciones, utilizando React para la interfaz de usuario. Realizo control de versiones con Git/GitHub y gestiono reuniones directas con los clientes para comprender y adaptar cada solución a sus necesidades, asegurando su satisfacción y el éxito de cada proyecto.',
        en: 'Responsible for the development of my own projects, mostly for Public Administration. I design and implement solutions with microservices architecture, using Java, Spring Boot, Docker, and databases (MySQL, OracleSQL). I develop both the backend and frontend of the applications, using React for the user interface. I manage version control with Git/GitHub and hold direct meetings with clients to understand and adapt each solution to their needs, ensuring their satisfaction and the success of each project.'
      }
    },
    {
      icon: <FaUserTie />,
      title: { es: 'Hermes Interactiva ', en: 'Hermes Interactiva' },
      role: { es: 'Desarrollador FullStack', en: 'FullStack Developer' },
      type: { es: 'Presencial', en: 'On-site' },
      date: { es: 'Abril 2024 – Junio 2024', en: 'April 2024 – June 2024' },
      place: { es: 'Cádiz, España', en: 'Cádiz, Spain' },
      desc: {
        es: 'Desarrollo de aplicaciones web completas con Java, Spring Boot, Angular, React y MySQL.',
        en: 'Development of complete web applications with Java, Spring Boot, Angular, React, and MySQL.'
      }
    },
  ];

  const education = [
    {
      icon: <FaGraduationCap color="#ff4c4c" />,
      title: { es: 'STAFF FORMACIÓN', en: 'STAFF FORMACIÓN' },
      type: { es: 'DAM', en: 'DAM' },
      date: { es: 'Septiembre 2022 – Abril 2024', en: 'September 2022 – April 2024' },
      place: { es: 'Cádiz, España', en: 'Cádiz, Spain' },
      desc: { es: 'Matrícula de Honor en el grado.', en: 'Honors degree.' }
    }
  ];

  const socialLinks = [
    { icon: <FaLinkedin />, url: 'https://linkedin.com/in/hugo-arevalo-gil', label: 'LinkedIn' },
    { icon: <FaGithub />, url: 'https://github.com/Hugoag03', label: 'GitHub' },
    { icon: <FaEnvelope />, url: 'https://mail.google.com/mail/?view=cm&to=hugoarevalogil08@gmail.com', label: 'Email' },
  ];

  const cvLinks = {
    es: "https://drive.google.com/file/d/1HDktNfPpP_i-dCesfz-syC7FJ766wI4A/view?usp=sharing",
    en: "https://drive.google.com/file/d/1uxkKgjWsG1__NtvvYHX9HPycjT2G-pFR/view?usp=sharing"
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 700;

  const projects = [
    {
      id: 'p1',
      title: { es: 'FactuBoost', en: 'FactuBoost' },
      desc: {
        es: 'Sistema de facturación con gestión de clientes, productos y facturas, estadísticas y panel de control. Pensado para pequeñas empresas que quieren dejar de depender de Excel y centralizar su facturación.',
        en: 'Billing system with customer, product and invoice management, plus statistics and a dashboard. Designed for small businesses that want to move away from Excel and centralise their invoicing.'
      },
      githubUrl: 'https://github.com/Hugoag03/factuboost',
      // Vídeo: pon aquí un enlace embed (YouTube/Vimeo) o un mp4 en /public
      video: { kind: 'mp4', url: '/factuboost-video/factuboost-video.mp4' } // kind: 'embed' | 'mp4'
    },
    {
      id: 'p2',
      title: { es: 'CoworkPilot', en: 'CoworkPilot' },
      desc: {
        es: 'Gestión de reservas de espacios de coworking, con calendario, establecimientos y control de disponibilidad por horas. Ayuda a mejorar la ocupación y evitar solapes entre reservas.',
        en: 'Coworking booking system with calendar, venues and hourly availability control. Helps improve occupancy and avoid overlapping reservations.'
      },
      githubUrl: 'https://github.com/Hugoag03/coworkingpilot',
      video: { kind: 'mp4', url: '/coworkpilot-video/coworkpilot-video.mp4' }
    },
    {
      id: 'p3',
      title: { es: 'TaskMirror', en: 'TaskMirror' },
      desc: {
        es: 'Plataforma de gestión de tareas y proyectos con roles (jefes de proyecto y equipo), asignación de trabajo y seguimiento del estado. Ideal para organizar mejor el trabajo del día a día.',
        en: 'Task and project management platform with roles (project managers and team), task assignment and status tracking. Ideal to organise day-to-day work more effectively.'
      },
      githubUrl: 'https://github.com/Hugoag03/taskmirror',
      video: { kind: 'mp4', url: '/taskmirror-video/taskmirror-video.mp4' }
    }
  ];

  const sectionVariantsLeft = {
    hidden: { opacity: 0, x: isMobile ? -4 : -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
  };
  const sectionVariantsRight = {
    hidden: { opacity: 0, x: isMobile ? 4 : 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
  };

  React.useEffect(() => {
    const enableAudio = () => setAudioEnabled(true);
    window.addEventListener('mousedown', enableAudio, { once: true });
    window.addEventListener('keydown', enableAudio, { once: true });
    return () => {
      window.removeEventListener('mousedown', enableAudio);
      window.removeEventListener('keydown', enableAudio);
    };
  }, []);

  React.useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  }, [menuOpen]);

  React.useEffect(() => {
    document.body.classList.toggle('light-mode', !darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  React.useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    if (slideAudio.current) {
      slideAudio.current.currentTime = 0;
      slideAudio.current.play();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const toggleLang = () => {
    setLang(l => l === 'es' ? 'en' : 'es');
    if (audioEnabled && langAudio.current) {
      langAudio.current.currentTime = 0;
      langAudio.current.play();
    }
  };

  const closeProjectModal = () => setActiveProject(null);

  // Función para reproducir el sonido
  const playHoverSound = () => {
    if (!audioEnabled) return;
    if (hoverAudio.current) {
      hoverAudio.current.currentTime = 0;
      hoverAudio.current.play();
    }
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setFormStatus(null);
    emailjs.sendForm('service_poja7l8', 'template_ok7yw3u', formRef.current, 'Zv4tENb-wOAK3Q-zf')
      .then((result) => {
        setFormStatus('success');
        formRef.current.reset();
        if (successAudio.current) {
          successAudio.current.currentTime = 0;
          successAudio.current.play();
        }
      }, (error) => {
        setFormStatus('error');
      });
  };

  // Scroll a sección y cierra acordeones de experiencia si aplica
  const scrollToSection = (id) => {
    if (id === 'experience') {
      setOpenExp([]);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 10);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const badgeActual = {
    es: 'Trabajo actual',
    en: 'Current job'
  };
  const badgePart = {
    es: 'Actual (tiempo parcial)',
    en: 'Current (part-time)'
  };

  const themeLabels = {
    es: {
      light: 'Cambiar a modo claro',
      dark: 'Cambiar a modo oscuro'
    },
    en: {
      light: 'Switch to light mode',
      dark: 'Switch to dark mode'
    }
  };

  return (
    <>
      {/* Audio para el hover del menú */}
      <audio ref={hoverAudio} src={process.env.PUBLIC_URL + '/hover.wav'} preload="auto" />
      {/* Audio para el toggle de modo claro/oscuro */}
      <audio ref={toggleAudio} src={process.env.PUBLIC_URL + '/toggle.wav'} preload="auto" />
      {/* Audio para el cambio de idioma */}
      <audio ref={langAudio} src={process.env.PUBLIC_URL + '/toggle.wav'} preload="auto" />
      {/* Audio para el scroll to top */}
      <audio ref={slideAudio} src={process.env.PUBLIC_URL + '/slide.wav'} preload="auto" />
      {/* LOGO FIJO IZQUIERDA */}
      <div className="Navbar-logo" id="logo-hag-fixed">HAG</div>
      {/* NAVBAR */}
      <nav className="Navbar">
        {/* Botón hamburguesa solo en móvil */}
        <button
          className="Hamburger-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <ul className={`Navbar-links${menuOpen ? ' open' : ''}`}>
          {navLinks.map(link => (
            <li key={link.id}>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  if (link.id === 'experience') {
                    setOpenExp([]);
                    setOpenEdu([]);
                  }
                  scrollToSection(link.id);
                }}
                onMouseEnter={playHoverSound}
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {/* Botón flotante modo claro/oscuro y bandera */}
      <div className="Float-controls">
        <button
          className="ModeToggle-float"
          onClick={() => {
            if (toggleAudio.current) {
              toggleAudio.current.currentTime = 0;
              toggleAudio.current.play();
            }
            setDarkMode(dm => !dm);
          }}
          aria-label={darkMode ? themeLabels[lang].light : themeLabels[lang].dark}
          title={darkMode ? themeLabels[lang].light : themeLabels[lang].dark}
        >
          {darkMode ? <FaSun /> : <FaMoon />}
        </button>
        <button
          className="LangToggle-float"
          onClick={toggleLang}
          aria-label={lang === 'es' ? 'Cambiar a inglés' : 'Switch to Spanish'}
          title={lang === 'es' ? 'Cambiar a inglés' : 'Switch to Spanish'}
        >
          <img
            src={process.env.PUBLIC_URL + '/translate.png'}
            alt="Translate"
          />
        </button>
      </div>
      {/* Botón scroll to top */}
      {showScrollTop && (
        <button className="ScrollTop-btn" onClick={scrollToTop} aria-label="Volver arriba">
          <FaArrowUp />
        </button>
      )}
      {/* HEADER / HOME */}
      <header className="Header" id="home">
        <div className="Header-grid">
          <motion.div className="Header-avatar-wrap" initial={{ opacity: 0, x: -60 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <img 
              src={process.env.PUBLIC_URL + (darkMode ? '/hugo.jpg' : '/hugo2.jpg')}
              alt="Hugo Arévalo Gil"
              className={`Header-avatar${darkMode ? '' : ' Header-avatar-noborder'}`}
            />
          </motion.div>
          <motion.div className="Header-info" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1>Hugo<br/>Arévalo Gil</h1>
            <h2 className="Header-title">{headerTitle[lang]}</h2>
            <p className="Header-desc">{headerDesc[lang]}</p>
            <div className="Header-socials">
              {socialLinks.map(link => (
                <a href={link.url} key={link.label} target="_blank" rel="noopener noreferrer" title={link.label} className="Header-social">{link.icon}</a>
              ))}
              <a
                href={cvLinks[lang]}
                target="_blank"
                rel="noopener noreferrer"
                title={translations[lang].cv}
                className="Header-social Header-cv-btn"
              >
                {translations[lang].cv}
              </a>
            </div>
          </motion.div>
        </div>
      </header>

      {/* ABOUT */}
      <motion.section className="About" id="about" initial="hidden" whileInView="visible" viewport={{}} variants={sectionVariantsLeft}>
        <h2>{translations[lang].aboutTitle}</h2>
        <p><strong>{translations[lang].aboutP1}</strong></p>
        <p>{translations[lang].aboutP2}</p>
        <p>{translations[lang].aboutP3}</p>
        <p>{translations[lang].aboutP4}</p>
      </motion.section>

      {/* SKILLS */}
      <motion.section className="Skills" id="skills" initial="hidden" whileInView="visible" viewport={{}} variants={sectionVariantsRight}>
        <h2>{translations[lang].skills}</h2>
        <div className="Skills-list">
          {skills.map((s, i) => (
            <motion.div className="Skill-chip" key={s.label} title={s.label} initial="hidden" whileInView="visible" viewport={{}} variants={i % 2 === 0 ? sectionVariantsLeft : sectionVariantsRight}>
              <span className="Skill-icon">{s.icon}</span>
              <span>{s.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* EXPERIENCE */}
      <motion.section className="Experience" id="experience" initial="hidden" whileInView="visible" viewport={{}} variants={sectionVariantsLeft}>
        <h2 style={{cursor: 'pointer'}} onClick={() => setOpenExp([])}>{translations[lang].experience}</h2>
        <div className="Timeline-list">
          {experience.map((exp, i) => {
            const isOpen = openExp.includes(i);
            return (
              <motion.div className="Timeline-item" key={i} initial="hidden" whileInView="visible" viewport={{}} variants={i % 2 === 0 ? sectionVariantsLeft : sectionVariantsRight}>
                <div className="Timeline-icon">{exp.icon}</div>
                <div className="Timeline-content">
                  <h3
                    style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}
                    onClick={() => {
                      playHoverSound();
                      setOpenExp(isOpen ? openExp.filter(idx => idx !== i) : [...openExp, i]);
                    }}
                  >
                    <span style={{marginRight: 8, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}>
                      <FaChevronDown />
                    </span>
                    <span>
                      {exp.title[lang]} ({exp.role[lang]}, {exp.type[lang]})
                      {exp.title[lang].toLowerCase().includes('indra') && (
                        <span className="badge-actual" style={{ marginLeft: 6 }}>{badgeActual[lang]}</span>
                      )}
                      {exp.title[lang].toLowerCase().includes('freelancer') && (
                        <span className="badge-actual-part" style={{ marginLeft: 6 }}>{badgePart[lang]}</span>
                      )}
                    </span>
                  </h3>
                  {isOpen && (
                    <>
                      <span className="Timeline-date">{exp.date[lang]} - {exp.place[lang]}</span>
                      <p>{exp.desc[lang]}</p>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
        <h2 style={{cursor: 'pointer'}} onClick={() => setOpenEdu([])}>Educación</h2>
        <div className="Timeline-list">
          {education.map((edu, i) => (
            <motion.div className="Timeline-item" key={i} initial="hidden" whileInView="visible" viewport={{}} variants={i % 2 === 0 ? sectionVariantsLeft : sectionVariantsRight}>
              <div className="Timeline-icon">{edu.icon}</div>
              <div className="Timeline-content">
                <h3
                  style={{cursor: 'pointer', display: 'flex', alignItems: 'center'}}
                  onClick={() => {
                    playHoverSound();
                    const isOpen = openEdu.includes(i);
                    setOpenEdu(isOpen ? openEdu.filter(idx => idx !== i) : [...openEdu, i]);
                  }}
                >
                  <span style={{marginRight: 8, transition: 'transform 0.2s', transform: openEdu.includes(i) ? 'rotate(180deg)' : 'rotate(0deg)'}}>
                    <FaChevronDown />
                  </span>
                  <span>
                    {edu.title[lang]} ({edu.type[lang]})
                  </span>
                </h3>
                {openEdu.includes(i) && (
                  <>
                    <span className="Timeline-date">{edu.date[lang]} - {edu.place[lang]}</span>
                    <p>{edu.desc[lang]}</p>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* PROJECTS */}
      <motion.section className="Projects" id="projects" initial="hidden" whileInView="visible" viewport={{}} variants={sectionVariantsRight}>
        <h2>{translations[lang].projects}</h2>
        <p className="Projects-intro">{translations[lang].projectsIntro}</p>
        <div className="Projects-meta">
          <div className="Projects-stackIntro">{translations[lang].projectsStackIntro}</div>
          <div className="Projects-moreInfo">{translations[lang].projectsMoreInfoNote}</div>
        </div>
        <div className="Projects-list">
          {projects.map((p, i) => (
            <motion.div className="Project-card" key={p.id} initial="hidden" whileInView="visible" viewport={{}} variants={i % 2 === 0 ? sectionVariantsLeft : sectionVariantsRight}>
              <div className="Project-head">
                <h3>{p.title[lang]}</h3>
              </div>
              <p className="Project-desc">{p.desc[lang]}</p>
              <div className="Project-actions">
                <button
                  className="Project-btn"
                  onClick={() => setActiveProject(p)}
                  disabled={!p.video?.url}
                  title={!p.video?.url ? 'Añade el enlace del vídeo para habilitarlo' : translations[lang].projectBtnVideo}
                >
                  <FaPlay style={{ marginRight: 8 }} />
                  {translations[lang].projectBtnVideo}
                </button>
                <a
                  className={`Project-btn Project-link${p.githubUrl ? '' : ' disabled'}`}
                  href={p.githubUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!p.githubUrl}
                  onClick={(e) => { if (!p.githubUrl) e.preventDefault(); }}
                  title={!p.githubUrl ? 'Añade el enlace a GitHub para habilitarlo' : translations[lang].projectBtnGithub}
                >
                  <FaGithub style={{ marginRight: 8 }} />
                  {translations[lang].projectBtnGithub}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* PROJECT VIDEO MODAL */}
      {activeProject && (
        <div className="ProjectModal" role="dialog" aria-modal="true" aria-label="Project video modal" onClick={closeProjectModal}>
          <div className="ProjectModal-content" onClick={(e) => e.stopPropagation()}>
            <button className="ProjectModal-close" onClick={closeProjectModal} aria-label="Cerrar">
              <FaTimes />
            </button>
            <div className="ProjectModal-title">{activeProject.title[lang]}</div>
            <div className="ProjectModal-media">
              {activeProject.video?.kind === 'mp4' ? (
                <video controls className="ProjectModal-video">
                  <source src={activeProject.video.url} />
                </video>
              ) : (
                <iframe
                  className="ProjectModal-iframe"
                  src={activeProject.video?.url}
                  title={activeProject.title[lang]}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* CONTACT */}
      <motion.section className="Contact" id="contact" initial="hidden" whileInView="visible" viewport={{}} variants={sectionVariantsLeft}>
        <h2>{translations[lang].contact}</h2>
        <form className="Contact-form" ref={formRef} onSubmit={sendEmail}>
          <input type="text" name="from_name" placeholder={translations[lang].name} required />
          <input type="email" name="from_email" placeholder={translations[lang].email} required />
          <textarea name="message" placeholder={translations[lang].message} required></textarea>
          <button type="submit">{translations[lang].send}</button>
        </form>
        {formStatus === 'success' && <p style={{color: '#4caf50', marginTop: '1rem'}}>{translations[lang].feedbackSuccess}</p>}
        {formStatus === 'error' && <p style={{color: '#ff4c4c', marginTop: '1rem'}}>{translations[lang].feedbackError}</p>}
        <p style={{ marginTop: '1rem', opacity: 0.9 }}>
          {lang === 'es'
            ? 'Si tienes una idea o un proyecto en mente, cuéntame qué necesitas y vemos cómo llevarlo a producción.'
            : 'If you have an idea or a project in mind, tell me what you need and we’ll see how to take it to production.'}
        </p>
        <div className="Contact-socials">
          {socialLinks.map(link => (
            <a href={link.url} key={link.label} target="_blank" rel="noopener noreferrer" title={link.label} className="Contact-social">{link.icon}</a>
          ))}
        </div>
      </motion.section>
    </>
  );
}
